/* ============================================================================
   haiku-motion — lerped page scroll + magnetic hover

   TWO EFFECTS, ONE LAYER, both additive and both self-disabling.

   1. SMOOTH SCROLL. The reference (Codrops InfiniteScrollGSAPGallery) does not
      use a scroll library: it tweens a plain number toward a target every frame
      and reads that number as the scroll position. Same idea here, applied to
      the window — wheel deltas accumulate into `target`, and `current` eases
      toward it by EASE. That single constant IS the feel.

      ⚠️ EASE IS PER 1/60 s, NOT PER FRAME (since 2026-10-03). It used to be
      applied once per animation frame, so the glide was twice as long on a
      60 Hz screen as on a 120 Hz one, and stretched further whenever a frame
      ran late — measured on the live site, one wheel notch took 240 ms to
      cover 90% at 120 Hz (and ~430 ms at 60 Hz). Now each frame covers
      1 − (1 − EASE)^(dt / 16.67 ms) of the remaining distance: the same glide
      on every screen, and a late frame catches up instead of lagging more.

      ⚠️ INTEROP. The "Last items" row captures wheel while its rail still has
      room and calls preventDefault. If this listener also consumed those
      events, the row and the page would fight over the same gesture. So we
      bail on any event where defaultPrevented is already true — the row wins
      its own gesture, and the page resumes the moment it lets go.

   2. MAGNETIC HOVER. Ported from the Dash.fi design system (Magnetic Hover,
      @dashfi/svelte/ui/magnetic-hover): translate = (cursorCentre −
      elementCentre) × strength, strength 0.3, 300ms ease, reset on mouseleave.
      Its own spec records two non-features — no focus mirroring and no
      reduced-motion handling — so both are handled at this call site rather
      than pretended away.

   FAIL-SAFE. Everything here is opt-in via body.hmo, set only after the
   listeners are attached. Nothing is hidden, nothing is removed; if this file
   never parses, the page scrolls natively and buttons sit still.
   ========================================================================== */
(function () {
  'use strict';

  var cfg = window.haikuMotionConfig || {};
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var coarse = window.matchMedia && window.matchMedia('(hover: none)').matches;

  var EASE       = typeof cfg.ease === 'number' ? cfg.ease : 0.085;
  var SMOOTH_MIN = typeof cfg.smoothMinWidth === 'number' ? cfg.smoothMinWidth : 1024;
  var STRENGTH   = typeof cfg.strength === 'number' ? cfg.strength : 0.3;
  var DURATION   = typeof cfg.duration === 'number' ? cfg.duration : 300;
  var MAG_SEL    = cfg.magneticSelector || '.hn-link, .menu-meta__link, .slide-heading a, .btn:not(#CheckOut), .button:not(#CheckOut)';

  /* ── 1. smooth scroll ──────────────────────────────────────────────────── */

  function initSmooth() {
    if (reduce || coarse) return false;
    if (window.innerWidth < SMOOTH_MIN) return false;
    /* ⚠️ Templates that drive their own scroll. The story gallery pages slides
       with GSAP Observer off the wheel gesture; this lerp also claims the
       wheel and preventDefaults it, over a document with ~64px of real scroll.
       Two systems, one gesture — that is the jam. See haiku-motion.liquid. */
    if (cfg.smoothSkip) return false;

    var target = window.scrollY;
    var current = target;
    var running = false;
    var raf = 0;
    var lastT = 0;                    /* the previous frame's timestamp; 0 = the loop is starting */

    function limit() {
      return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    }

    function frame(now) {
      /* Time-based (see the header): a first frame counts as one 60 Hz frame,
         and a stall of more than 100 ms is treated as 100 ms, which with any
         EASE worth having lands the page anyway. */
      var dt = lastT ? Math.min(100, now - lastT) : 16.667;
      lastT = now;
      current += (target - current) * (1 - Math.pow(1 - EASE, dt / 16.667));
      // Land exactly rather than asymptotically, or scrollY never settles.
      if (Math.abs(target - current) < 0.4) {
        current = target;
        window.scrollTo(0, current);
        running = false;
        raf = 0;
        lastT = 0;
        return;
      }
      window.scrollTo(0, current);
      raf = requestAnimationFrame(frame);
    }

    function onWheel(e) {
      // The products row (or any other capture) already owns this gesture.
      if (e.defaultPrevented) { target = window.scrollY; current = target; return; }
      if (e.ctrlKey) return;                       // pinch-zoom
      if (e.deltaMode !== 0) return;               // line/page mode: leave native

      /* ⚠️ NOTHING TO SMOOTH, SO DO NOT TAKE THE GESTURE.

         The collection page does not scroll: its WebGL gallery is a fixed
         viewport that consumes the wheel itself. Measured there —
         documentElement.scrollHeight <= innerHeight, no inner overflow
         scroller anywhere on the page, and all six wheel events of a gesture
         coming back defaultPrevented.

         The exemption below only ever looked for a scrollable ANCESTOR, and
         there isn't one — the gallery is not an overflow box, it is a canvas
         reading the wheel. So this handler ran on every gesture, called
         preventDefault, clamped its target to a limit of zero, and span its
         rAF loop for a page it could not move: two systems on one gesture,
         one of them doing nothing.

         `limit() === 0` is the honest test for "this page has no scroll of
         its own", and it costs one layout read that the frame loop already
         does. */
      if (limit() <= 0) return;

      /* ⚠️ A SIDEWAYS GESTURE IS NEVER THE PAGE'S.
         This listener preventDefaults every wheel event it does not bail on,
         and the exemption below only ever looked at overflow-Y. The last-items
         rail scrolls on overflow-X, so a two-finger horizontal swipe over it
         was cancelled here and moved nothing -- measured, a 400px horizontal
         wheel over the rail produced 0px of scroll and the event came back
         with defaultPrevented true. The row read as draggable but not
         scrollable, and there was nothing wrong with the row.
         Adding deltaX to a vertical target would be wrong anyway: on a
         horizontal-dominant gesture the page has nothing to do. */
      var sideways = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      if (sideways) return;

      /* An open modal dialog (the newsletter, haiku-newsletter.js) owns the
         wheel: the page behind is overflow-locked and must not glide under it. */
      if (e.target && e.target.closest && e.target.closest('dialog[open]')) return;

      var el = e.target;
      // Respect any genuinely scrollable inner surface (drawers, selects) —
      // on BOTH axes, so a rail keeps a gesture aimed at it.
      while (el && el !== document.body) {
        var cs = getComputedStyle(el);
        var oy = cs.overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 2) return;
        var ox = cs.overflowX;
        if ((ox === 'auto' || ox === 'scroll') && el.scrollWidth > el.clientWidth + 2 && e.deltaX) return;
        el = el.parentElement;
      }
      e.preventDefault();
      if (!running) { current = window.scrollY; target = current; running = true; }
      target = Math.min(limit(), Math.max(0, target + e.deltaY));
      if (!raf) { lastT = 0; raf = requestAnimationFrame(frame); }
    }

    // Anything that jumps the page must resync, or the next wheel snaps back.
    function resync() { target = window.scrollY; current = target; }

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', resync);
    window.addEventListener('resize', resync, { passive: true });
    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest && e.target.closest('a[href^="#"]');
      if (a) setTimeout(resync, 60);
    });
    return true;
  }

  /* ── 2. magnetic hover ─────────────────────────────────────────────────── */

  var magneticOn = !(reduce || coarse);           // spec ships neither guard

  function magnetise(el) {
    if (el.__hmo) return false;
    el.__hmo = true;
    el.style.transition = 'transform ' + DURATION + 'ms ease';
    el.style.willChange = 'transform';

    el.addEventListener('pointermove', function (e) {
      var r = el.getBoundingClientRect();
      var dx = (e.clientX - (r.left + r.width / 2)) * STRENGTH;
      var dy = (e.clientY - (r.top + r.height / 2)) * STRENGTH;
      el.style.transform = 'translate3d(' + dx.toFixed(2) + 'px,' + dy.toFixed(2) + 'px,0)';
    });

    el.addEventListener('pointerleave', function () {
      el.style.transform = '';
    });
    // The spec has no focus mirror; keyboard users get no motion, but they
    // must not inherit a stuck offset from a previous pointer visit.
    el.addEventListener('blur', function () { el.style.transform = ''; });
    return true;
  }

  /* ⚠️ ONE SCAN AT BOOT SEES ONLY WHAT EXISTS AT BOOT. This file is a deferred
     script in <head>, so it runs before any body script has built its markup:
     haiku-likes.js makes every heart after this, and `.hl-heart` sat in the
     selector with 0 of 17 hearts attached (measured 2026-09-26). A script
     that adds matching markup later calls window.HaikuMotion.attach(root).

     Idempotent through the __hmo expando rather than an attribute: cloneNode
     copies attributes but not listeners, so a marked clone would be skipped
     while having no magnet at all. Off (reduced motion, no hover) = no-op.

     `selector` (optional) narrows the pass: an element attaches only if it
     matches BOTH the configured selector and this one. A late caller names
     what it made — haiku-likes.js passes '.hl-heart' — so it cannot sweep up
     other late matches. attach(d) once pulled 8 homepage elements the boot
     scan had left still (4 hidden a.slide-heading, 4 a.slide-button incl. the
     visible "see wardrobe"), measured 2026-09-26. */
  function attachMagnetic(root, selector) {
    if (!magneticOn || !root || !root.querySelectorAll) return 0;
    var narrow = selector && selector !== MAG_SEL ? selector : null;
    function fitsMagnet(el) {
      return el.matches(MAG_SEL) && (!narrow || el.matches(narrow));
    }
    var n = 0;
    if (root.nodeType === 1 && root.matches && fitsMagnet(root) && magnetise(root)) n++;
    var nodes = root.querySelectorAll(narrow || MAG_SEL);
    for (var i = 0; i < nodes.length; i++) if (fitsMagnet(nodes[i]) && magnetise(nodes[i])) n++;
    if (n && document.body) { document.body.classList.add('hmo'); document.body.classList.add('hmo-magnetic'); }
    return n;
  }

  window.HaikuMotion = { attach: attachMagnetic };

  function boot() {
    var a = initSmooth();
    var b = attachMagnetic(document) > 0;
    if (a || b) document.body.classList.add('hmo');
    if (a) document.body.classList.add('hmo-smooth');
    if (b) document.body.classList.add('hmo-magnetic');
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
